import { createContext } from 'react';

import { Article, Issue, Style } from '@/constants';

/**
 * The issue/article *structure to render* -- split out of the old, single
 * `pages/issue/Issue.context.tsx` (improvements/07-template-architecture.md's
 * "views -> pages/widgets sort" left a real `widgets/ -> pages/` back-reference
 * here, since `widgets/issue-reader/*` needed this data but `IssueContext`
 * lived in `pages/issue/`, above `widgets/` in the FSD layer order).
 *
 * Deliberately narrow: pure domain data (the issue, its articles, the
 * currently-selected article/language, and the entity-level setters that
 * just change *which* piece of that data is selected) -- not reader-UI
 * chrome state (view mode, font size, favorites, audio player), which stays
 * in `widgets/issue-reader/model/ReaderStateContext.ts` instead, since that's
 * genuinely reader-widget state, not issue data. `pages/issue/Issue.tsx`
 * provides real values into both; `widgets/issue-reader/*` and
 * `entities/issue`'s own consumers only ever read this one downward.
 */
// The online reader's route prefix -- the default every consumer already
// built assuming (`putInternalLink` in `widgets/issue-reader/responsive/
// responsive.hooks.ts`), before a second reader (the Dexie-backed
// `/downloads/issues/$issueId/$articleId` route, `pages/downloaded-issue`)
// existed to need a different one. See `IssueContextProps.basePath` below.
export const ONLINE_ISSUE_BASE_PATH = '/issues';

export const IssueContext = createContext<IssueContextProps>({
	language: 'en',
	issue: null,
	currentArticle: null,
	stylesMap: new Map(),
	setCurrentArticle: () => {},
	articles: [],
	articlesMap: undefined,
	languages: new Set<string>(),
	articleLanguages: new Set<string>(),
	disabledLanguages: new Set<string>(),
	setLanguage: () => {},
	navigateToArticle: undefined,
	basePath: ONLINE_ISSUE_BASE_PATH,
	reloadIssue: undefined,
});

export interface IssueContextProps {
	language: string;
	issue: Issue | null;
	currentArticle: Article | null;
	stylesMap: Map<string, Style>;
	articles: Array<Article>;
	setCurrentArticle: (article: Article) => void;
	articlesMap: Map<string, Article> | undefined;
	languages: Set<string>;
	articleLanguages: Set<string>;
	disabledLanguages: Set<string>;
	setLanguage: (lang: string) => void;
  navigateToArticle?: (articleId: number) => void;
	/**
	 * The route prefix this reader instance is mounted under --
	 * `ONLINE_ISSUE_BASE_PATH` ('/issues') by default, or
	 * `DOWNLOADED_ISSUE_BASE_PATH` ('/downloads/issues', `@entities/
	 * downloaded-issue`) for the Dexie-backed reader. The handful of spots
	 * that build an in-app issue/article URL (`putInternalLink`, the
	 * redirect-to-first-article effect) read this instead of hardcoding
	 * '/issues', so the same widget tree links correctly under either
	 * route. Also doubles as the signal a widget uses to skip menu entries
	 * that don't make sense offline (Share/Favorite/Audio/Open Original
	 * PDF) -- compare against `DOWNLOADED_ISSUE_BASE_PATH`, don't add a
	 * second flag for the same distinction.
	 */
	basePath: string;
	/**
	 * Re-reads this issue from its data source and re-renders -- only ever
	 * set by the Dexie-backed reader (`pages/downloaded-issue/model/
	 * use-downloaded-issue-view.ts`), called by `RefreshContentButton`
	 * (`widgets/issue-reader/menu/menu-button.tsx`) once `entities/
	 * downloaded-issue`'s `refreshDownloadedIssue` has re-saved fresh
	 * content to Dexie, so the screen actually shows what was just
	 * fetched instead of the pre-refresh copy still in state. `undefined`
	 * for the online reader, which has no equivalent "reload from
	 * elsewhere" concept -- its content is already live.
	 */
	reloadIssue?: () => void;
}
